import { useState } from 'react';
import type { TestReview, Verdict } from '@sr-test-wizard/shared';
import { VERDICT_CSS } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { useRunStore } from '../../state/runStore';

interface Props {
  runId: string;
  tests: TestReview[];
  currentIndex: number;
  onSelect: (index: number) => void;
  filter: string;
  onFilter: (v: string) => void;
}

function dotClass(verdict: Verdict | null): string {
  if (!verdict) return 'empty';
  return VERDICT_CSS[verdict];
}

function effectiveVerdict(t: TestReview): Verdict | null {
  return t.tester_verdict ?? t.ai_verdict ?? null;
}

export function SidebarTestsList({ runId, tests, currentIndex, onSelect, filter, onFilter }: Props) {
  const analyzingTestKeys = useRunStore((s) => s.analyzingTestKeys);
  const setTestAnalyzing = useRunStore((s) => s.setTestAnalyzing);
  const analysisInputs = useRunStore((s) => s.analysisInputs);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const analyzingSet = new Set(analyzingTestKeys);

  const analyzeOne = async (testKey: string) => {
    setPendingKey(testKey);
    setTestAnalyzing(testKey, true);
    try {
      await api.post(`/api/runs/${runId}/tests/${testKey}/analyze`, {
        ...(analysisInputs ? { inputs: analysisInputs } : {}),
      });
    } catch {
      setTestAnalyzing(testKey, false);
    } finally {
      setPendingKey((current) => (current === testKey ? null : current));
    }
  };

  const norm = filter.trim().toLowerCase();
  const filtered = norm
    ? tests.filter(
        (t) =>
          t.test_key.toLowerCase().includes(norm) ||
          t.test_summary.toLowerCase().includes(norm) ||
          (effectiveVerdict(t) ?? '').toLowerCase().includes(norm),
      )
    : tests;

  const summary = tests.reduce(
    (acc, t) => {
      const v = effectiveVerdict(t);
      if (!v) acc.unanalyzed++;
      else acc[VERDICT_CSS[v] as keyof typeof acc]++;
      return acc;
    },
    { pass: 0, fail: 0, 'in-game': 0, na: 0, unanalyzed: 0 } as Record<string, number>,
  );

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <input
          placeholder="Filter by key, summary, or verdict"
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
        />
        <div className="row small muted" style={{ flexWrap: 'nowrap', gap: 4, overflowX: 'auto' }}>
          <span className="badge">TBA {summary.unanalyzed}</span>
          <span className="badge in-game">In-Game {summary['in-game']}</span>
          <span className="badge good">Pass {summary.pass}</span>
          <span className="badge bad">Fail {summary.fail}</span>
          <span className="badge grey">N/A {summary.na}</span>
        </div>
      </div>
      <div className="sidebar-list">
        {filtered.length === 0 && (
          <p className="muted small" style={{ padding: 12 }}>
            No tests match the filter.
          </p>
        )}
        {filtered.map((test) => {
          const idx = tests.findIndex((t) => t.test_key === test.test_key);
          const verdict = effectiveVerdict(test);
          const isActive = idx === currentIndex;
          const hasAi = !!test.ai_verdict;
          const isAnalyzing = analyzingSet.has(test.test_key) || pendingKey === test.test_key;
          return (
            <div
              key={test.test_key}
              className={`test-item status-${dotClass(verdict)} ${isActive ? 'active' : ''} ${isAnalyzing ? 'analyzing' : ''}`}
              onClick={() => onSelect(idx)}
            >
              {isAnalyzing ? (
                <span
                  className="test-item-spinner"
                  title="AI analysis in progress"
                  aria-label="analyzing"
                />
              ) : (
                <span className={`dot ${dotClass(verdict)}`} title={verdict ?? 'not analyzed'} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="test-key">{test.test_key}</div>
                <div className="test-summary" title={test.test_summary}>
                  {test.test_summary}
                </div>
                <div className="test-meta">
                  {isAnalyzing && (
                    <span className="badge analyzing small" title="AI analysis in progress">
                      Analyzing...
                    </span>
                  )}
                  {test.xray_status && <span className="badge small">{test.xray_status}</span>}
                  {test.bug_key && <span className="badge bug small">{test.bug_key}</span>}
                  {test.tester_verdict && test.tester_verdict !== test.ai_verdict && (
                    <span className="badge info small" title="Tester override">
                      override
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="test-item-analyze"
                title={hasAi ? 'Re-run AI for this test' : 'Run AI for this test'}
                disabled={isAnalyzing}
                onClick={(e) => {
                  e.stopPropagation();
                  void analyzeOne(test.test_key);
                }}
              >
                {isAnalyzing ? '...' : hasAi ? '\u21BB' : 'AI'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
